import { Input, InputRef } from 'antd';
import Icon from 'assets/Icons';
import Box from 'components/Box';
import Form, { FormItem } from 'components/Form';
import Modal from 'components/Modal';
import CurrencyKeyBoard, { CurrencyKeyBoardRef } from 'components/NumberKeyBoard/Currency';
import Spin from 'components/Spin';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import { IGiftCardPaymentValue, IGiftCardV3PaymentValue } from 'features/cashier/services/types/giftCard';
import CountingFooter from 'features/payment/components/CountingFooter';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import useGetInfoGiftCard from './useGetInfoGiftCard';
import ModalGiftCardPaymentV2 from './ModalGiftCardPaymentV2';
import { useDollarAmountInputRef } from 'components/DollarAmount/Input';
import { IBillDetailData } from 'features/payment/services/types/bill';
import { sumBy } from 'lodash';
import { ModalChooseNextPaymentRef } from 'features/payment/pages/PaymentPage/CashierSide/components/GiftCard/ModalChooseNextPayment';

type ModalPaymentGiftCardProps = {
  visible?: boolean;
  onClose?: () => void;
  onSubmit?: (val: IGiftCardPaymentValue) => void;
  onSubmitV3?: (val: IGiftCardV3PaymentValue) => void;
  total?: number;
  v2?: boolean;
  keepState?: boolean;
  detail?: IBillDetailData | null;
  giftCardPayments?: IGiftCardV3PaymentValue[];
  onDeleteItem?: (o: IGiftCardV3PaymentValue) => void;
  onPayment: (giftCards: IGiftCardV3PaymentValue[]) => void;
  onRedeemGiftCard: (val: IGiftCardV3PaymentValue) => void;
  onContinueWithMultiple: (giftCards: IGiftCardV3PaymentValue[]) => void;
  modalChooseNextPaymentRef?: React.RefObject<ModalChooseNextPaymentRef>
};

export const useModalPaymentGiftCardRef = () => useRef<ModalPaymentGiftCardRefs>(null);

export type ModalPaymentGiftCardRefs = {
  setAmount: (num: number) => void;
  reset: (focusGift?: boolean) => void;
};
const ModalPaymentGiftCard = forwardRef<ModalPaymentGiftCardRefs, ModalPaymentGiftCardProps>(
  ({ v2, visible,
    onClose = () => undefined,
    onSubmitV3 = () => undefined,
    onSubmit = () => undefined,
    keepState, detail, giftCardPayments, onDeleteItem,
    onPayment,
    onRedeemGiftCard = () => undefined,
    onContinueWithMultiple = () => undefined,
    modalChooseNextPaymentRef,
  }, ref) => {
    const currencyRef = useRef<CurrencyKeyBoardRef>(null);
    const dollarRef = useDollarAmountInputRef();
    const inputCodeRef = useRef<InputRef>(null);
    const [amount, setAmount] = useState<number | null>(null);
    const giftCard = useGetInfoGiftCard();

    const remaining = useMemo(() => {
      const payment = sumBy(giftCardPayments, o => o.amount) || 0;
      const total = detail?.total || 0;
      const _remaining = total - payment;
      return _remaining > 0 ? _remaining : 0;
    }, [detail?.total, giftCardPayments]);

    useEffect(() => {
      if (keepState) return;
      if (visible) {
        setTimeout(() => { inputCodeRef.current?.focus(); }, 100);
      }
    }, [visible]);

    useEffect(() => {
      if (!giftCard.data) return;
      const available = giftCard.resultGiftCard.available;
      const amountVal = available - remaining < 0 ? available : remaining;
      const valueStr = amountVal?.toFixed(2)?.toString();
      currencyRef.current?.setValue(valueStr);
      dollarRef.current?.init(amountVal ? formatCurrency(amountVal) : undefined);
    }, [giftCard.data, remaining]);

    const amountValue = amount || 0;
    const giftCardBalance = giftCard.data ? giftCard.resultGiftCard.available - amountValue : 0;

    // Redeeming the same card again replaces its previous line, so that line's amount is payable again.
    const sameCardAmount = giftCard.data ? (giftCardPayments?.find(o => o.giftCardId === giftCard.data?.id)?.amount || 0) : 0;
    const maxRedeemAmount = remaining + sameCardAmount;
    // Compare in cents to avoid floating point noise (e.g. 0.1 + 0.2).
    const isOverRemaining = !!giftCard.data && Math.round(amountValue * 100) > Math.round(maxRedeemAmount * 100);
    const amountMsg = isOverRemaining ? `Amount cannot exceed remaining ${formatCurrency(maxRedeemAmount)}` : undefined;
    const isValidAmount = !!(amountValue && giftCard.data && (giftCardBalance >= 0) && !isOverRemaining);

    const handleClose = () => {
      onClose();
      if (keepState) return;
      giftCard.reset();
      currencyRef.current?.setValue('');
      dollarRef.current?.clear();
    };

    const handleSubmit = () => {
      if (!giftCard.data || isOverRemaining) return;

      onSubmitV3({
        giftCardId: giftCard.data.id,
        amount: amountValue,
        data: giftCard.data,
        resultGiftCard: giftCard.resultGiftCard,
        giftCardBalance,
        code: giftCard.code,
      });
      onSubmit({
        giftCardId: giftCard.data.id,
        amount: amountValue,
      });
    };

    const handleRedeem = () => {
      if (!giftCard.data) {
        const currentAmount = sumBy(giftCardPayments, o => o.amount);
        if (currentAmount > 0) {
          return onContinueWithMultiple(giftCardPayments || []);
        }
        return;
      }
      if (!isValidAmount) return;
      onRedeemGiftCard({
        giftCardId: giftCard.data.id,
        amount: amountValue,
        data: giftCard.data,
        resultGiftCard: giftCard.resultGiftCard,
        giftCardBalance,
        code: giftCard.code,
      });
    };

    useImperativeHandle(ref, () => ({
      setAmount: num => {
        currencyRef.current?.setValue(num.toFixed(2));
        if (keepState) {
          dollarRef.current?.init(num ? formatCurrency(num) : undefined);
        } else {
          dollarRef.current?.init();
        }
      },
      reset: (focusGift?: boolean) => {
        giftCard.reset();
        currencyRef.current?.setValue('0.00');
        if (focusGift) {
          dollarRef.current?.clear();
          setTimeout(() => { inputCodeRef.current?.focus(); }, 100);
        } else {
          dollarRef.current?.focus('');
        }
      }
    }));

    const onScan = () => {
      giftCard.reset();
      setTimeout(() => { inputCodeRef.current?.focus(); }, 100);
    };

    if (v2) return <ModalGiftCardPaymentV2
      visible={visible} onClose={handleClose}
      amount={amount}
      setAmount={setAmount}
      currencyRef={currencyRef}
      dollarRef={dollarRef}
      onPayment={onPayment}
      onRedeem={handleRedeem}
      isValid={isValidAmount}
      amountMsg={amountMsg}
      giftCard={giftCard}
      inputCodeRef={inputCodeRef}
      onScan={onScan}
      giftCardBalance={giftCardBalance}
      detail={detail}
      giftCardPayments={giftCardPayments}
      onDeleteItem={onDeleteItem}
      remaining={remaining}
      modalChooseNextPaymentRef={modalChooseNextPaymentRef}
    />;

    return (
      <Modal
        // width="auto"
        footerBgColor={colorTheme.fill_5}
        headerBgColor={colorTheme.info_bg_3}
        visible={visible}
        modalTitle="GIFT CARD"
        width={800}
        noneBodyStyle
        footer={
          <CountingFooter
            submitButtonTitle="Redeem"
            handleCloseModal={handleClose}
            onSubmit={handleSubmit}
            isValid={!!amountValue && !isOverRemaining}
            style={{ padding: '1.5rem', paddingTop: '1rem' }}
          >
            <Box className="center">
              <Box display="flex" alignItems="center" gap="4">
                <Text variant="CONTENT_1" color="text_3">
                  Gift Card Balance:
                </Text>
                <Text variant="H5" color="text_3">
                  {formatCurrency(giftCardBalance > 0 ? giftCardBalance : 0)}
                </Text>
              </Box>
            </Box>
            <Text mb={1} />
          </CountingFooter>
        }
      >
        <ModalPaymentGiftCardStyled>
          <Box>
            <Box display="flex" flexDirection="column" gap="2">
              <FormItem label={'Gift Number'}
                validateStatus={giftCard.msg ? 'warning' : undefined}
                help={giftCard.msg}
              >
                <Input
                  ref={inputCodeRef}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  addonAfter={
                    <button type='button' onClick={onScan}><Icon type={'QR'} /></button>
                  }
                  value={giftCard.code}
                  onChange={e => giftCard.setCode(e.target.value)}
                />
              </FormItem>
              {giftCard.loading && <Spin />}
            </Box>

            <Box display="flex" gap="2" justifyContent='space-between' mb='2'>
              <Box display="flex" alignItems='center' gap='2'>
                <Text variant="CONTENT_1" color="text_3">
                  Available:
                </Text>
                <Text variant="H9" color="text_3">
                  {formatCurrency(giftCard.resultGiftCard.available)}
                </Text>
              </Box>
              <Box br='line_1' />
              <Box display="flex" alignItems='center' gap='2'>
                <Text variant="CONTENT_1" color="text_3">
                  Used:
                </Text>
                <Text variant="H9" color="text_3">
                  {formatCurrency(giftCard.resultGiftCard.used)}
                </Text>
              </Box>
              <Box br='line_1' />
              <Box display="flex" alignItems='center' gap='2'>
                <Text variant="CONTENT_1" color="text_3">
                  Amount:
                </Text>
                <Text variant="H9" color="text_3">
                  {formatCurrency(giftCard.resultGiftCard.amount)}
                </Text>
              </Box>
              <Box br='line_1' />
              <Box display="flex" alignItems='center' gap='2'>
                <Text variant="CONTENT_1" color="text_3">
                  Expiration:
                </Text>
                <Text variant="H9" color="text_3">
                  {giftCard.resultGiftCard.expireDate}
                </Text>
              </Box>
            </Box>


            <Text variant="H6" color="text_3">
              Redeem
            </Text>
            <CurrencyKeyBoard ref={currencyRef} value={amount} onChange={val => setAmount(val || 0)} />
            {/* <CurrencyInputWrapStyled>
              <CurrencyInput
                ref={currencyInputRef}
                onChangeDebounce={(e, val) => setAmount(val)}
              />
            </CurrencyInputWrapStyled> */}
          </Box>
        </ModalPaymentGiftCardStyled>
      </Modal>
    );
  });
ModalPaymentGiftCard.displayName = 'ModalPaymentGiftCard';
export default ModalPaymentGiftCard;

const ModalPaymentGiftCardStyled = styled(Form)`
  padding: 1rem 3rem;
`;
